import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Bot,
  ArrowLeft,
  Download,
  Search,
  Info,
  TriangleAlert,
  Check,
  X,
} from "lucide-react";
import type { Prompt } from "./PromptsPage";
import { ExtendedTable } from "@/components/ExtendedTable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { countries } from "../countries";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  buildExecutionRunRef,
  ExecutionDetailsSheet,
  type ExecutionDetailsRunRef,
  exportToCSV,
  promptsDetailsCitationsColumns,
  promptsDetailsColumns,
  promptStatus,
  WebQueriesColumns,
} from "../services/helpers";
import { optionsPlatforms } from "@/Dashboard";
import { ChartAreaInteractive } from "@/components/Chart";
import { SimpleTable } from "@/components/SimpleTable";
import { Input } from "@/components/ui/input";
import {
  type ChartData,
  type PaginatedResponseMeta,
  competitorsAPI,
  onboardingGuideAPI,
  promptsAPI,
  visabilityAPI,
} from "@/services/api.tsx";
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "@/components/AppSidebar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useVisibilityRealtime } from "@/hooks/useVisibilityRealtime";
import { useCompetitorsRealtime } from "@/hooks/useCompetitorsRealtime";
import { canonicalizeDomain } from "@/utils/domain";
import {
  mapPlatformsToProviders,
  mapProvidersToPlatforms,
} from "@/utils/providers";
import {
  buildBrandKeyVariants,
  isDomainLikeValue,
  normalizeBrandKey,
} from "@/lib/brandAliases";
import { capturePosthogEvent, toPlatformValue } from "@/lib/posthog";
import { normalizeUrlTypeValue, toUrlTypeLabel } from "@/lib/urlTypes";
import { useDashboardFiltersStore } from "@/store/useDashboardFiltersStore";
import {
  buildPromptsDetailsExecutionsTableData,
  normalizeProviderRunKey,
  toExecutionDateKey,
} from "@/lib/promptsDetailsExecutions";
import {
  buildPromptExecutionsExportRows,
  buildPromptSourcesExportRows,
} from "@/lib/promptsDetailsExports";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";
import { Spinner } from "@/components/ui/spinner";
import {
  DateRangeFilter,
  type DateRangeValue,
  getDateRangeForPreset,
} from "@/components/DateRangeFilter";
import { shouldPrefetchPaginatedPage } from "@/lib/paginationCache";

const defaultData: ChartData = {
  chartData: [],
  citedDomainsVisibility: {},
  citedBrandsVisibility: {},
  competitorsVisibility: {},
  citedPagesStats: {},
  mentionedPagesStats: {},
  data: [],
  mainBrandVisibility: null,
  promptStatus: "idle",
  promptLastRun: null,
  promptProviderStatusHistory: [],
  promptRunCount: 0,
  webQueries: [],
};

const defaultPaginationMeta: PaginatedResponseMeta = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const defaultCompetitors: [] = [];

type SourceDomainType =
  "Publisher" | "Competitor" | "Community" | "Owned" | "Other";

const SOURCE_DOMAIN_TYPES: SourceDomainType[] = [
  "Publisher",
  "Competitor",
  "Community",
  "Owned",
  "Other",
];

const SOURCE_DOMAIN_TYPE_COLORS: Record<SourceDomainType, string> = {
  Publisher: "#3DB9CF",
  Competitor: "#6E56CF",
  Community: "#FFC53D",
  Owned: "#FF5E00",
  Other: "#E5E5E5",
};

const normalizeSourceDomainType = (
  value?: string | null,
  domainLike?: string | null,
): SourceDomainType => {
  const type = String(value || "")
    .trim()
    .toLowerCase();
  const domain = canonicalizeDomain(domainLike || "") || "";
  if (
    domain === "youtube.com" ||
    domain.endsWith(".youtube.com") ||
    domain === "youtu.be"
  ) {
    return "Community";
  }
  if (type.includes("publisher")) return "Publisher";
  if (type.includes("competitor")) return "Competitor";
  if (
    type.includes("community") ||
    type.includes("social") ||
    type.includes("forum") ||
    type.includes("ugc")
  ) {
    return "Community";
  }
  if (type.includes("owned") || type.includes("owner")) return "Owned";
  if (
    type.includes("listicle") ||
    type.includes("directory") ||
    type.includes("how-to") ||
    type.includes("guide") ||
    type.includes("review") ||
    type.includes("alternatives") ||
    type.includes("comparison") ||
    type.includes("blog") ||
    type.includes("article") ||
    type.includes("resource")
  ) {
    return "Publisher";
  }
  return "Other";
};

const domainsMatch = (
  domain?: string | null,
  candidate?: string | null,
): boolean => {
  const normalizedDomain = canonicalizeDomain(domain || "") || "";
  const normalizedCandidate = canonicalizeDomain(candidate || "") || "";
  if (!normalizedDomain || !normalizedCandidate) return false;
  return (
    normalizedDomain === normalizedCandidate ||
    normalizedDomain.endsWith(`.${normalizedCandidate}`)
  );
};

export default function PromptsDetails({
  prompt,
  currentBrand,
  globalSelectedProviders,
  globalTimeRange,
  globalDateRange,
  isOpen,
  onOpenChange,
  onTimeRangeChange,
  onDateRangeChange,
}: {
  prompt: Prompt;
  currentBrand: Brand;
  globalSelectedProviders?: string[];
  globalTimeRange?: string;
  globalDateRange?: DateRangeValue;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onTimeRangeChange?: (value: string) => void;
  onDateRangeChange?: (value: DateRangeValue) => void;
}) {
  const { t } = useLocale();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [sorting2, setSorting2] = useState<SortingState>([]);
  const selectedPlatforms = useDashboardFiltersStore(
    (state) => state.selectedPlatforms,
  );
  const setSelectedPlatforms = useDashboardFiltersStore(
    (state) => state.setSelectedPlatforms,
  );
  const [localTimeRange, setLocalTimeRange] = useState("7d");
  const [localDateRange, setLocalDateRange] = useState(() =>
    getDateRangeForPreset("7d"),
  );
  const timeRange = globalTimeRange ?? localTimeRange;
  const dateRange = globalDateRange ?? localDateRange;
  const setTimeRange = onTimeRangeChange ?? setLocalTimeRange;
  const setDateRange = onDateRangeChange ?? setLocalDateRange;
  const handleSetTimeRange = (value: string) => {
    setTimeRange(value);
    if (["1d", "7d", "14d", "30d"].includes(value)) {
      setDateRange(getDateRangeForPreset(value));
    }
  };
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnFilters2, setColumnFilters2] = useState<ColumnFiltersState>([]);
  const [search, setSearch] = useState("");
  const [executionsPagination, setExecutionsPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });
  const [sourcesPagination, setSourcesPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [executionDetailsOpenRun, setExecutionDetailsOpenRun] =
    useState<ExecutionDetailsRunRef | null>(null);
  const [isExecutionsExporting, setIsExecutionsExporting] = useState(false);
  const [isSourcesExporting, setIsSourcesExporting] = useState(false);
  const sheetOpen = Boolean(isOpen);
  const [tab, setTab] = useState("executions");
  const [status, setStatus] = useState(
    prompt.is_active ? "active" : "inactive",
  );
  const normalizedDays = dateRange.days || parseInt(timeRange);
  const baseMainBrandKey = normalizeBrandKey(currentBrand?.name);
  const globalSelectedPlatforms = useMemo(() => {
    const mapped = mapProvidersToPlatforms(globalSelectedProviders);
    return mapped ?? [];
  }, [globalSelectedProviders]);
  const localSelectedProviders = useMemo(() => {
    return mapPlatformsToProviders(selectedPlatforms);
  }, [selectedPlatforms]);
  const effectiveSelectedProviders = useMemo(() => {
    if (localSelectedProviders?.length) return localSelectedProviders;
    if (globalSelectedProviders?.length) return globalSelectedProviders;
    return undefined;
  }, [localSelectedProviders, globalSelectedProviders]);
  const queryKey = [
    "visability",
    currentBrand?.brandId,
    timeRange,
    `${dateRange.startDate}:${dateRange.endDate}`,
    prompt.id,
    effectiveSelectedProviders?.join(",") || "all",
    "summary",
  ];
  const requestDateRange =
    dateRange.preset === "custom" || dateRange.preset === "yesterday"
      ? dateRange
      : undefined;
  const { data = defaultData, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await visabilityAPI.getRunResults(
        currentBrand,
        normalizedDays,
        effectiveSelectedProviders,
        prompt.id,
        false,
        false,
        requestDateRange,
        false,
        false,
      );
      return res.data;
    },
    enabled: !!currentBrand?.brandId && !!timeRange && sheetOpen,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const expectedProviders = useMemo(() => {
    if (effectiveSelectedProviders?.length) return effectiveSelectedProviders;
    const promptProviders = Array.isArray(data?.promptProviders)
      ? data.promptProviders
      : [];
    return promptProviders.length ? promptProviders : undefined;
  }, [effectiveSelectedProviders, data?.promptProviders]);
  const promptDetailsSearch = search.trim();
  const {
    data: executionsPage = {
      executions: [],
      pagination: defaultPaginationMeta,
      promptProviderStatusHistory: [],
      promptProviders: [],
    },
    isLoading: isExecutionsLoading,
    isFetching: isExecutionsFetching,
  } = useQuery({
    queryKey: [
      "promptExecutions",
      currentBrand?.brandId,
      prompt.id,
      normalizedDays,
      `${dateRange.startDate}:${dateRange.endDate}`,
      effectiveSelectedProviders?.join(",") || "all",
      executionsPagination.pageIndex,
      executionsPagination.pageSize,
      promptDetailsSearch,
    ],
    queryFn: () =>
      visabilityAPI.getPromptExecutions({
        brandId: currentBrand.brandId,
        days: normalizedDays,
        providers: effectiveSelectedProviders,
        promptId: prompt.id,
        page: executionsPagination.pageIndex + 1,
        pageSize: executionsPagination.pageSize,
        search: promptDetailsSearch,
        dateRange: requestDateRange,
      }),
    enabled: !!currentBrand?.brandId && !!prompt.id && sheetOpen,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const executionsPaginationMeta =
    executionsPage.pagination || defaultPaginationMeta;
  const {
    data: sourcesPage = { sources: [], pagination: defaultPaginationMeta },
    isLoading: isSourcesLoading,
    isFetching: isSourcesFetching,
  } = useQuery({
    queryKey: [
      "promptSources",
      currentBrand?.brandId,
      prompt.id,
      normalizedDays,
      `${dateRange.startDate}:${dateRange.endDate}`,
      effectiveSelectedProviders?.join(",") || "all",
      sourcesPagination.pageIndex,
      sourcesPagination.pageSize,
      promptDetailsSearch,
    ],
    queryFn: () =>
      visabilityAPI.getPromptSources({
        brandId: currentBrand.brandId,
        days: normalizedDays,
        providers: effectiveSelectedProviders,
        promptId: prompt.id,
        page: sourcesPagination.pageIndex + 1,
        pageSize: sourcesPagination.pageSize,
        search: promptDetailsSearch,
        dateRange: requestDateRange,
      }),
    enabled: !!currentBrand?.brandId && !!prompt.id && sheetOpen,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const sourcesPaginationMeta = sourcesPage.pagination || defaultPaginationMeta;
  const availablePlatformOptions = useMemo(() => {
    const promptProviders = Array.isArray(data?.promptProviders)
      ? data.promptProviders
      : [];
    const mappedPromptPlatforms =
      mapProvidersToPlatforms(promptProviders) ?? [];
    const allowedPlatforms = new Set<string>([
      ...mappedPromptPlatforms,
      ...globalSelectedPlatforms,
      ...selectedPlatforms,
    ]);
    if (!allowedPlatforms.size) return optionsPlatforms;
    return optionsPlatforms.filter((option) =>
      allowedPlatforms.has(option.value),
    );
  }, [data?.promptProviders, globalSelectedPlatforms, selectedPlatforms]);

  useEffect(() => {
    if (!sheetOpen) return;
    if (selectedPlatforms.length) return;
    if (!globalSelectedPlatforms.length) return;
    const initialPlatforms = globalSelectedPlatforms.filter((platform) =>
      availablePlatformOptions.some((option) => option.value === platform),
    );
    if (initialPlatforms.length) {
      setSelectedPlatforms(initialPlatforms);
    }
  }, [
    sheetOpen,
    selectedPlatforms.length,
    globalSelectedPlatforms,
    availablePlatformOptions,
    setSelectedPlatforms,
  ]);

  useEffect(() => {
    setExecutionsPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
    setSourcesPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [
    currentBrand?.brandId,
    prompt.id,
    normalizedDays,
    dateRange.startDate,
    dateRange.endDate,
    effectiveSelectedProviders,
    promptDetailsSearch,
  ]);

  useEffect(() => {
    const filtered = selectedPlatforms.filter((platform) =>
      availablePlatformOptions.some((option) => option.value === platform),
    );
    if (filtered.length !== selectedPlatforms.length) {
      setSelectedPlatforms(filtered);
    }
  }, [availablePlatformOptions, selectedPlatforms, setSelectedPlatforms]);

  useVisibilityRealtime({
    brandId: currentBrand?.brandId,
    days: normalizedDays,
    providers: effectiveSelectedProviders,
    promptId: prompt.id,
    includeRunResponses: false,
    queryKey,
    enabled:
      !!currentBrand?.brandId &&
      !!timeRange &&
      sheetOpen &&
      dateRange.preset !== "custom" &&
      dateRange.preset !== "yesterday",
    keepAliveAfterFirstEnable: true,
    resubscribeOnEnable: true,
  });

  const mainBrandKey = useMemo(() => {
    const runs = Array.isArray(data.data) ? data.data : [];
    const counts = new Map<string, number>();
    runs.forEach((run: any) => {
      const brands = Array.isArray(run?.citedBrands) ? run.citedBrands : [];
      brands.forEach((brand: any) => {
        if (!brand?.isMain) return;
        const key = normalizeBrandKey(
          brand?.brandKey || brand?.brandDisplay || "",
        );
        if (!key) return;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      });
    });
    if (counts.size) {
      const [top] = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
      if (top?.[0]) return top[0];
    }
    return baseMainBrandKey;
  }, [data.data, baseMainBrandKey]);

  const {
    data: competitorsData = defaultCompetitors,
    isLoading: isCompetitorsLoading,
  } = useQuery({
    queryKey: ["competitors", currentBrand.brandId],
    queryFn: async () => {
      const res = await competitorsAPI.getCompetitors(currentBrand.brandId);
      return res.competitors || [];
    },
    enabled: !!currentBrand.brandId,
    staleTime: Infinity,
  });

  useCompetitorsRealtime({
    brandId: currentBrand?.brandId,
    queryKey: ["competitors", currentBrand.brandId],
    enabled: Boolean(sheetOpen && currentBrand?.brandId),
  });

  const brandKeyLabels = useMemo(() => {
    const map = new Map<string, string>();
    if (mainBrandKey) {
      map.set(mainBrandKey, currentBrand?.name || mainBrandKey);
    }
    competitorsData.forEach((competitor: any) => {
      const key = normalizeBrandKey(
        competitor.brandKey || competitor.brandDisplay || competitor.domain,
      );
      if (!key) return;
      const label =
        competitor.brandDisplay ||
        competitor.brandKey ||
        competitor.domain ||
        key;
      map.set(key, label);
    });
    return map;
  }, [competitorsData, currentBrand?.name, mainBrandKey]);

  const brandKeyToDomain = useMemo(() => {
    const map = new Map<string, string>();
    if (mainBrandKey && currentBrand?.url) {
      map.set(mainBrandKey, currentBrand.url);
    }
    competitorsData.forEach((competitor: any) => {
      const key = normalizeBrandKey(
        competitor.brandKey || competitor.brandDisplay || competitor.domain,
      );
      if (!key) return;
      if (competitor.domain) {
        map.set(key, competitor.domain);
      }
    });
    return map;
  }, [competitorsData, currentBrand?.url, mainBrandKey]);

  const brandKeyToChartLabel = useMemo(() => {
    const map = new Map<string, string>();
    if (mainBrandKey) {
      const base = canonicalizeDomain(currentBrand?.url) || "";
      if (base) {
        map.set(mainBrandKey, base);
      }
    }
    competitorsData.forEach((competitor: any) => {
      const key = normalizeBrandKey(
        competitor.brandKey || competitor.brandDisplay || competitor.domain,
      );
      if (!key) return;
      const domain = canonicalizeDomain(competitor.domain);
      if (domain) {
        map.set(key, domain);
      }
    });
    return map;
  }, [competitorsData, currentBrand?.url, mainBrandKey]);

  const trackedCompetitorDomains = useMemo(() => {
    const set = new Set<string>();
    competitorsData.forEach((competitor: any) => {
      if (competitor?.isOwnBrand) return;
      const status = String(competitor?.status || "")
        .trim()
        .toLowerCase();
      if (status && status !== "tracked") return;
      const domain = canonicalizeDomain(competitor?.domain || "");
      if (domain) {
        set.add(domain);
      }
    });
    return set;
  }, [competitorsData]);

  const currentBrandDomain = useMemo(() => {
    return (
      brandKeyToChartLabel.get(mainBrandKey) ||
      canonicalizeDomain(currentBrand?.url) ||
      canonicalizeDomain(brandKeyToDomain.get(mainBrandKey)) ||
      brandKeyLabels.get(mainBrandKey) ||
      mainBrandKey ||
      ""
    );
  }, [
    brandKeyLabels,
    brandKeyToChartLabel,
    brandKeyToDomain,
    currentBrand?.url,
    mainBrandKey,
  ]);

  const ownBrandDomains = useMemo(() => {
    const set = new Set<string>();
    const currentDomain = canonicalizeDomain(currentBrand?.url || "");
    if (currentDomain) {
      set.add(currentDomain);
    }
    const resolvedCurrentDomain = canonicalizeDomain(currentBrandDomain || "");
    if (resolvedCurrentDomain) {
      set.add(resolvedCurrentDomain);
    }
    competitorsData.forEach((competitor: any) => {
      if (!competitor?.isOwnBrand && !competitor?.isMain) return;
      const domain = canonicalizeDomain(competitor?.domain || "");
      if (domain) {
        set.add(domain);
      }
    });
    return set;
  }, [competitorsData, currentBrand?.url, currentBrandDomain]);

  const currentBrandMentionKeys = useMemo(() => {
    const set = new Set<string>();
    [
      mainBrandKey,
      currentBrand?.name,
      currentBrand?.url,
      currentBrandDomain,
      ...Array.from(ownBrandDomains),
    ].forEach((value) => {
      buildBrandKeyVariants(value).forEach((variant) => {
        if (variant) set.add(variant);
      });
      const domain = canonicalizeDomain(value || "");
      if (domain) {
        set.add(normalizeBrandKey(domain));
        set.add(normalizeBrandKey(domain.split(".")[0] || ""));
      }
    });
    return set;
  }, [
    currentBrand?.name,
    currentBrand?.url,
    currentBrandDomain,
    mainBrandKey,
    ownBrandDomains,
  ]);

  const isCurrentBrandMentionRef = useCallback(
    (brandRef: any): boolean => {
      if (!brandRef) return false;
      if (brandRef?.isMain === true) return true;

      const textValues = [
        brandRef?.name,
        brandRef?.key,
        brandRef?.brandName,
        brandRef?.brandDisplay,
      ];
      const matchesName = textValues.some((value) =>
        buildBrandKeyVariants(value).some((variant) =>
          currentBrandMentionKeys.has(variant),
        ),
      );
      if (matchesName) return true;

      const domainValues = [
        brandRef?.url,
        brandRef?.domain,
        brandRef?.website,
        brandRef?.key,
        brandRef?.name,
      ];
      return domainValues.some((value) => {
        const domain = canonicalizeDomain(value || "");
        if (!domain) return false;
        return Array.from(ownBrandDomains).some((ownDomain) =>
          domainsMatch(domain, ownDomain),
        );
      });
    },
    [currentBrandMentionKeys, ownBrandDomains],
  );

  const resolveSourceType = useCallback(
    (type?: string | null, domainLike?: string | null): SourceDomainType => {
      const domain = canonicalizeDomain(domainLike || "") || "";
      if (domain && ownBrandDomains.has(domain)) {
        return "Owned";
      }
      if (domain && trackedCompetitorDomains.has(domain)) {
        return "Competitor";
      }
      return normalizeSourceDomainType(type, domainLike);
    },
    [ownBrandDomains, trackedCompetitorDomains],
  );

  const selectedBrandKeys = useMemo(() => {
    const set = new Set<string>();
    if (mainBrandKey) {
      set.add(mainBrandKey);
    }
    competitorsData.forEach((competitor: any) => {
      const key = normalizeBrandKey(
        competitor.brandKey || competitor.brandDisplay || competitor.domain,
      );
      if (key) set.add(key);
    });
    return set;
  }, [mainBrandKey, competitorsData]);

  const competitorAliasToKey = useMemo(() => {
    const map = new Map<string, string>();
    const addAlias = (value: string | null | undefined, canonical: string) => {
      buildBrandKeyVariants(value).forEach((variant) => {
        if (!variant || map.has(variant)) return;
        map.set(variant, canonical);
      });
    };
    if (mainBrandKey) {
      addAlias(mainBrandKey, mainBrandKey);
      addAlias(currentBrand?.name, mainBrandKey);
      addAlias(currentBrand?.url, mainBrandKey);
    }
    competitorsData.forEach((competitor: any) => {
      const canonical = normalizeBrandKey(
        competitor.brandKey || competitor.brandDisplay || competitor.domain,
      );
      if (!canonical) return;
      addAlias(canonical, canonical);
      addAlias(competitor.brandKey, canonical);
      addAlias(competitor.brandDisplay, canonical);
      addAlias(competitor.domain, canonical);
    });
    return map;
  }, [competitorsData, currentBrand?.name, currentBrand?.url, mainBrandKey]);

  const resolveCompetitorKey = (
    value?: string | null,
    isMain?: boolean,
  ): string => {
    const variants = buildBrandKeyVariants(value);
    for (const variant of variants) {
      const resolved = competitorAliasToKey.get(variant);
      if (resolved) return resolved;
    }
    if (isMain && mainBrandKey && !isDomainLikeValue(value))
      return mainBrandKey;
    return normalizeBrandKey(value);
  };

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnVisibility2, setColumnVisibility2] = useState<VisibilityState>(
    {},
  );
  const [rowSelection2, setRowSelection2] = useState({});
  const [citationsRowSelection, setCitationsRowSelection] = useState({});
  const [rowSelectionWebQueries, setRowSelectionWebQueries] = useState({});
  const queryClient = useQueryClient();
  const shouldInvalidatePrompts = useRef(false);
  const promptResultTrackedRef = useRef<string>("");
  const onboardingPromptResultStepTrackedRef = useRef<string>("");
  const citationsTrackedRef = useRef<string>("");
  const promptTabTrackedRef = useRef<string>("");

  useEffect(() => {
    if (
      !sheetOpen ||
      !currentBrand?.brandId ||
      !prompt.id ||
      !executionsPaginationMeta.hasNextPage ||
      !shouldPrefetchPaginatedPage(executionsPagination.pageIndex)
    ) {
      return;
    }
    const nextPageIndex = executionsPagination.pageIndex + 1;
    void queryClient.prefetchQuery({
      queryKey: [
        "promptExecutions",
        currentBrand.brandId,
        prompt.id,
        normalizedDays,
        `${dateRange.startDate}:${dateRange.endDate}`,
        effectiveSelectedProviders?.join(",") || "all",
        nextPageIndex,
        executionsPagination.pageSize,
        promptDetailsSearch,
      ],
      queryFn: () =>
        visabilityAPI.getPromptExecutions({
          brandId: currentBrand.brandId,
          days: normalizedDays,
          providers: effectiveSelectedProviders,
          promptId: prompt.id,
          page: nextPageIndex + 1,
          pageSize: executionsPagination.pageSize,
          search: promptDetailsSearch,
          dateRange: requestDateRange,
        }),
      staleTime: Infinity,
      gcTime: Infinity,
    });
  }, [
    currentBrand?.brandId,
    dateRange.endDate,
    dateRange.startDate,
    effectiveSelectedProviders,
    executionsPagination.pageIndex,
    executionsPagination.pageSize,
    executionsPaginationMeta.hasNextPage,
    normalizedDays,
    prompt.id,
    promptDetailsSearch,
    queryClient,
    requestDateRange,
    sheetOpen,
  ]);

  useEffect(() => {
    if (
      !sheetOpen ||
      !currentBrand?.brandId ||
      !prompt.id ||
      !sourcesPaginationMeta.hasNextPage ||
      !shouldPrefetchPaginatedPage(sourcesPagination.pageIndex)
    ) {
      return;
    }
    const nextPageIndex = sourcesPagination.pageIndex + 1;
    void queryClient.prefetchQuery({
      queryKey: [
        "promptSources",
        currentBrand.brandId,
        prompt.id,
        normalizedDays,
        `${dateRange.startDate}:${dateRange.endDate}`,
        effectiveSelectedProviders?.join(",") || "all",
        nextPageIndex,
        sourcesPagination.pageSize,
        promptDetailsSearch,
      ],
      queryFn: () =>
        visabilityAPI.getPromptSources({
          brandId: currentBrand.brandId,
          days: normalizedDays,
          providers: effectiveSelectedProviders,
          promptId: prompt.id,
          page: nextPageIndex + 1,
          pageSize: sourcesPagination.pageSize,
          search: promptDetailsSearch,
          dateRange: requestDateRange,
        }),
      staleTime: Infinity,
      gcTime: Infinity,
    });
  }, [
    currentBrand?.brandId,
    dateRange.endDate,
    dateRange.startDate,
    effectiveSelectedProviders,
    normalizedDays,
    prompt.id,
    promptDetailsSearch,
    queryClient,
    requestDateRange,
    sheetOpen,
    sourcesPagination.pageIndex,
    sourcesPagination.pageSize,
    sourcesPaginationMeta.hasNextPage,
  ]);

  const changeStatusMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await promptsAPI.updatePromptStatus(data, data.id);
      return res.prompts;
    },
    onSuccess: (_, variables) => {
      setStatus(variables.isActive ? "active" : "inactive");
      shouldInvalidatePrompts.current = true;
      toast.success(t("legacy.the.prompt.is.updated.3d3fg7"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Check className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    },
    onError: () => {
      toast.error(t("legacy.couldnt.update.prompt.kvic7x"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    },
  });

  useEffect(() => {
    if (shouldInvalidatePrompts.current === true && sheetOpen === false) {
      queryClient.invalidateQueries({ queryKey: ["prompts"] });
      shouldInvalidatePrompts.current = false;
    }
  }, [sheetOpen, queryClient]);

  useEffect(() => {
    if (sheetOpen) return;
    promptResultTrackedRef.current = "";
    onboardingPromptResultStepTrackedRef.current = "";
    citationsTrackedRef.current = "";
    setExecutionDetailsOpenRun(null);
  }, [sheetOpen]);

  const handleExecutionDetailsOpenChange = (
    runRef: ExecutionDetailsRunRef,
    open: boolean,
  ) => {
    setExecutionDetailsOpenRun((prev) => {
      if (open) return runRef;
      if (!prev) return null;
      const matchesRunId = Boolean(runRef.runId && prev.runId === runRef.runId);
      const matchesFallback = Boolean(
        runRef.fallbackRunKey && prev.fallbackRunKey === runRef.fallbackRunKey,
      );
      const matchesBaseFallback = Boolean(
        runRef.baseFallbackRunKey &&
        prev.baseFallbackRunKey === runRef.baseFallbackRunKey,
      );
      return matchesRunId || matchesFallback || matchesBaseFallback
        ? null
        : prev;
    });
  };

  useEffect(() => {
    if (!sheetOpen || isLoading || !currentBrand?.brandId) return;
    const promptId = String(prompt.id || "");
    if (!promptId || promptResultTrackedRef.current === promptId) return;

    const citationsCount = sourcesPaginationMeta.total;
    const hasCompetitorMentions = (sourcesPage.sources || []).some(
      (entry: any) =>
        String(entry?.domainType || "")
          .toLowerCase()
          .includes("competitor") && Boolean(entry?.mentioned),
    );

    promptResultTrackedRef.current = promptId;
    capturePosthogEvent("prompt_result_viewed", {
      platform: toPlatformValue(
        effectiveSelectedProviders || data.promptProviders || [],
      ),
      brand_id: currentBrand.brandId,
      prompt_id: promptId,
      citations_count: citationsCount,
      has_competitor_mentions: hasCompetitorMentions,
    });
    if (onboardingPromptResultStepTrackedRef.current !== promptId) {
      onboardingPromptResultStepTrackedRef.current = promptId;
      void onboardingGuideAPI
        .updateState({
          brandId: currentBrand.brandId,
          stepName: "review_prompt_results",
          completionSource: "user",
        })
        .then((res) => {
          if (res.completedStepName) {
            capturePosthogEvent("onboarding_guide_step_completed", {
              step_name: res.completedStepName,
              completion_source: res.completionSource || "user",
            });
          }
          queryClient.invalidateQueries({
            queryKey: ["onboardingGuide", currentBrand.brandId],
          });
        })
        .catch(() => {});
    }
  }, [
    currentBrand?.brandId,
    data.promptProviders,
    effectiveSelectedProviders,
    isLoading,
    prompt.id,
    sheetOpen,
    sourcesPage.sources,
    sourcesPaginationMeta.total,
  ]);

  useEffect(() => {
    if (
      !sheetOpen ||
      isLoading ||
      tab !== "citations" ||
      !currentBrand?.brandId
    ) {
      return;
    }
    const promptId = String(prompt.id || "");
    if (!promptId || citationsTrackedRef.current === promptId) return;

    citationsTrackedRef.current = promptId;
    capturePosthogEvent("citations_viewed", {
      prompt_id: promptId,
      platform: toPlatformValue(
        effectiveSelectedProviders || data.promptProviders || [],
      ),
      cited_domains_count: Object.keys(data.citedDomainsVisibility || {})
        .length,
    });
  }, [
    currentBrand?.brandId,
    data.citedDomainsVisibility,
    data.promptProviders,
    effectiveSelectedProviders,
    isLoading,
    prompt.id,
    sheetOpen,
    tab,
  ]);

  useEffect(() => {
    if (!sheetOpen || !currentBrand?.brandId) return;
    const promptId = String(prompt.id || "");
    const currentTab = String(tab || "")
      .trim()
      .toLowerCase();
    if (!promptId || !currentTab) return;
    const trackedKey = `${promptId}:${currentTab}`;
    if (promptTabTrackedRef.current === trackedKey) return;
    promptTabTrackedRef.current = trackedKey;
    capturePosthogEvent("prompt_details_tab_viewed", {
      brand_id: currentBrand.brandId,
      prompt_id: promptId,
      tab: currentTab,
    });
  }, [currentBrand?.brandId, prompt.id, sheetOpen, tab]);

  const handleStatusChange = (value: string) => {
    const updatedPrompt = {
      text: prompt.text,
      country: prompt.country,
      id: prompt.id,
      isActive: value === "inactive" ? false : true,
    };

    changeStatusMutation.mutate(updatedPrompt);
  };

  const webQueries: { query: string; provider: string }[] = useMemo(() => {
    return Array.isArray(data.webQueries) ? data.webQueries : [];
  }, [data.webQueries]);

  const executionsTableData: any = useMemo(() => {
    return buildPromptsDetailsExecutionsTableData({
      runs: Array.isArray(executionsPage.executions)
        ? executionsPage.executions
        : [],
      promptProviderStatusHistory: Array.isArray(
        executionsPage.promptProviderStatusHistory,
      )
        ? executionsPage.promptProviderStatusHistory
        : [],
      expectedProviders,
      search,
      includePlaceholders: false,
    });
  }, [
    executionsPage.executions,
    executionsPage.promptProviderStatusHistory,
    expectedProviders,
    search,
  ]);

  useEffect(() => {
    if (!executionDetailsOpenRun || executionDetailsOpenRun.runId) return;
    const targetBaseFallback = String(
      executionDetailsOpenRun.baseFallbackRunKey || "",
    ).trim();
    if (!targetBaseFallback) return;

    const matchingCompletedRun = executionsTableData.find((run: any) => {
      const runId = String(run?.id || "").trim();
      if (!runId) return false;

      const providerValue = normalizeProviderRunKey(run?.provider);
      const runDateValue = toExecutionDateKey(
        run?.runDate || run?.createdAt || run?.created_at,
      );
      if (!providerValue || !runDateValue) return false;

      const baseFallbackRunKey = [providerValue, runDateValue]
        .filter(Boolean)
        .join("|");
      return baseFallbackRunKey === targetBaseFallback;
    });

    const nextRunId = String(matchingCompletedRun?.id || "").trim();
    if (!nextRunId) return;

    const providerValue = normalizeProviderRunKey(
      matchingCompletedRun?.provider,
    );
    const runDateValue = toExecutionDateKey(
      matchingCompletedRun?.runDate ||
        matchingCompletedRun?.createdAt ||
        matchingCompletedRun?.created_at,
    );
    const createdAtValue = String(
      matchingCompletedRun?.createdAt || matchingCompletedRun?.created_at || "",
    ).trim();
    const fallbackRunKey = [providerValue, runDateValue, createdAtValue]
      .filter(Boolean)
      .join("|");

    setExecutionDetailsOpenRun((prev) => {
      if (!prev || prev.runId) return prev;
      if (prev.baseFallbackRunKey !== targetBaseFallback) return prev;
      return {
        runId: nextRunId,
        fallbackRunKey: fallbackRunKey || targetBaseFallback,
        baseFallbackRunKey: targetBaseFallback,
      };
    });
  }, [executionDetailsOpenRun, executionsTableData]);

  const selectedExecutionSummaryRun = useMemo(() => {
    if (!executionDetailsOpenRun) return null;
    return (
      executionsTableData.find((run: any) => {
        const currentRef = buildExecutionRunRef(run || {});
        const hasRunIdMatch = Boolean(
          executionDetailsOpenRun.runId &&
          currentRef.runId &&
          executionDetailsOpenRun.runId === currentRef.runId,
        );
        if (hasRunIdMatch) return true;
        const hasFallbackMatch = Boolean(
          executionDetailsOpenRun.fallbackRunKey &&
          currentRef.fallbackRunKey &&
          executionDetailsOpenRun.fallbackRunKey === currentRef.fallbackRunKey,
        );
        if (hasFallbackMatch) return true;
        const hasBaseFallbackMatch = Boolean(
          !executionDetailsOpenRun.runId &&
          !currentRef.runId &&
          executionDetailsOpenRun.baseFallbackRunKey &&
          currentRef.baseFallbackRunKey &&
          executionDetailsOpenRun.baseFallbackRunKey ===
            currentRef.baseFallbackRunKey,
        );
        return hasBaseFallbackMatch;
      }) || null
    );
  }, [executionDetailsOpenRun, executionsTableData]);
  const executionDetailsRunId = String(
    selectedExecutionSummaryRun?.id || "",
  ).trim();
  const { data: executionDetailsData, isError: isExecutionDetailsError } =
    useQuery({
      queryKey: [
        "promptRunDetails",
        currentBrand?.brandId,
        executionDetailsRunId,
      ],
      queryFn: async () => {
        const res = await visabilityAPI.getPromptRunDetails({
          brandId: currentBrand.brandId,
          runId: executionDetailsRunId,
        });
        return res.run;
      },
      enabled: Boolean(
        sheetOpen &&
        executionDetailsOpenRun &&
        currentBrand?.brandId &&
        executionDetailsRunId,
      ),
      staleTime: Infinity,
    });
  const isExecutionDetailsResponseLoading = Boolean(
    executionDetailsRunId && !executionDetailsData && !isExecutionDetailsError,
  );
  const selectedExecutionDetailsRun = useMemo(() => {
    if (!selectedExecutionSummaryRun) return null;
    if (!executionDetailsData) return selectedExecutionSummaryRun;
    return {
      ...selectedExecutionSummaryRun,
      ...executionDetailsData,
    };
  }, [executionDetailsData, selectedExecutionSummaryRun]);

  const executionsNoDataText = useMemo(() => {
    const hasRuns = executionsPaginationMeta.total > 0;
    const isSearching = Boolean(search.trim());
    if (isSearching && hasRuns) {
      return {
        title: "No executions match your search",
        description: "Clear the search to see all executions.",
      };
    }
    return {
      title: "No executions yet",
      description:
        "We haven't run this prompt yet. Results will appear after the first execution.",
    };
  }, [executionsPaginationMeta.total, search]);

  const table = useReactTable({
    data: executionsTableData,
    columns: promptsDetailsColumns(mainBrandKey || undefined, {
      brandKeyLabels,
      selectedBrandKeys,
      resolveBrandKey: resolveCompetitorKey,
      onExecutionDetailsOpenChange: handleExecutionDetailsOpenChange,
    }),
    onSortingChange: setSorting2,
    onColumnFiltersChange: setColumnFilters2,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: executionsPaginationMeta.totalPages,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility2,
    onRowSelectionChange: setRowSelection2,
    onPaginationChange: setExecutionsPagination,
    state: {
      pagination: executionsPagination,
      sorting: sorting2,
      columnFilters: columnFilters2,
      columnVisibility: columnVisibility2,
      rowSelection: rowSelection2,
    },
  });

  const allQueriesTable = useReactTable({
    data: webQueries,
    columns: WebQueriesColumns,
    autoResetPageIndex: false,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelectionWebQueries,
    state: {
      sorting,
      columnFilters,
      rowSelection: rowSelectionWebQueries,
    },
  });

  const mapSourceRowsForPromptDetails = useCallback(
    (sources: any[]) => {
      return (Array.isArray(sources) ? sources : [])
        .map((source: any) => {
          const page = String(source?.url || "");
          const mentionedBrands = Array.isArray(source?.mentionedBrands)
            ? source.mentionedBrands.map((brand: any) => {
                const brandKey = normalizeBrandKey(
                  brand?.key || brand?.name || "",
                );
                const mappedUrl =
                  brand?.url ||
                  (brandKey ? brandKeyToDomain.get(brandKey) : "");
                const mainUrl =
                  brand?.isMain && currentBrand?.url ? currentBrand.url : "";
                return {
                  ...brand,
                  url: mappedUrl || mainUrl || brand?.url,
                };
              })
            : [];
          const urlTypeValue = normalizeUrlTypeValue(source?.urlType);
          return {
            brandId: currentBrand?.brandId || currentBrand?.id || "",
            url: page,
            used: source?.used,
            type: toUrlTypeLabel(urlTypeValue),
            urlType: urlTypeValue,
            domainType: resolveSourceType(source?.domainType, page),
            mentioned: mentionedBrands.some(isCurrentBrandMentionRef),
            mentionedBrands,
            updatedAt: source?.updatedAt ?? null,
            isSnapshotStale: Boolean(source?.isSnapshotStale),
            isSnapshotPending: Boolean(source?.isSnapshotPending),
            isManualRefreshRateLimited: Boolean(
              source?.isManualRefreshRateLimited,
            ),
            manualRefreshAvailableAt: source?.manualRefreshAvailableAt ?? null,
            snapshotDetails: source?.snapshotDetails ?? null,
            youtubeDetails: source?.youtubeDetails ?? null,
            platforms: Array.isArray(source?.platforms) ? source.platforms : [],
            promptId: prompt.id,
          };
        })
        .filter((source: any) => Boolean(source.url));
    },
    [
      currentBrand?.brandId,
      currentBrand?.id,
      currentBrand?.url,
      prompt.id,
      brandKeyToDomain,
      resolveSourceType,
      isCurrentBrandMentionRef,
    ],
  );

  const citationsPagesTable: any = useMemo(() => {
    return mapSourceRowsForPromptDetails(sourcesPage.sources);
  }, [sourcesPage.sources, mapSourceRowsForPromptDetails]);

  const sourcesByDomainTypeData = useMemo(() => {
    const totalsByType = new Map<SourceDomainType, number>();
    const domainUsageByType = new Map<SourceDomainType, Map<string, number>>();
    const summaryRows = Array.isArray((sourcesPage as any)?.sourceTypeSummary)
      ? (sourcesPage as any).sourceTypeSummary
      : [];
    const sourceRows = summaryRows.length
      ? summaryRows.map((source: any) => ({
          url: String(source?.url || ""),
          used: source?.used,
          domainType: source?.domainType,
        }))
      : citationsPagesTable;

    SOURCE_DOMAIN_TYPES.forEach((type) => {
      totalsByType.set(type, 0);
      domainUsageByType.set(type, new Map<string, number>());
    });

    sourceRows.forEach((source: any) => {
      const pageUrl = String(source?.url || "");
      const total = Number(source?.used);
      if (!Number.isFinite(total) || total <= 0) return;
      const domain =
        canonicalizeDomain(pageUrl) || String(pageUrl || "").trim();
      if (!domain) return;
      const type = resolveSourceType(source?.domainType, pageUrl);
      totalsByType.set(type, (totalsByType.get(type) || 0) + total);

      const typeDomainUsage = domainUsageByType.get(type)!;
      typeDomainUsage.set(domain, (typeDomainUsage.get(domain) || 0) + total);
    });

    const grandTotal = Array.from(totalsByType.values()).reduce(
      (sum, value) => sum + value,
      0,
    );

    return SOURCE_DOMAIN_TYPES.map((type) => {
      const total = totalsByType.get(type) || 0;
      const percentage =
        grandTotal > 0 ? Number(((total / grandTotal) * 100).toFixed(1)) : 0;
      const typeDomainUsage = domainUsageByType.get(type)!;
      const topDomains = Array.from(typeDomainUsage.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([domain]) => domain);
      return {
        type,
        total,
        percentage,
        color: SOURCE_DOMAIN_TYPE_COLORS[type],
        topDomains,
      };
    }).sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.type.localeCompare(b.type);
    });
  }, [citationsPagesTable, resolveSourceType, sourcesPage]);

  const hasSourcesByDomainTypeData = useMemo(
    () => sourcesByDomainTypeData.some((item) => item.total > 0),
    [sourcesByDomainTypeData],
  );

  const citationsNoDataText = useMemo(() => {
    const hasCitations = sourcesPaginationMeta.total > 0;
    const isSearching = Boolean(search.trim());
    if (isSearching && hasCitations) {
      return {
        title: "No sources match your search",
        description: "Clear the search to see all cited pages.",
      };
    }
    return {
      title: "No sources yet",
      description:
        "We haven't detected any cited pages for this prompt. Results will appear after the next execution.",
    };
  }, [sourcesPaginationMeta.total, search]);

  const citationsTable = useReactTable({
    data: citationsPagesTable,
    columns: promptsDetailsCitationsColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: sourcesPaginationMeta.totalPages,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setCitationsRowSelection,
    onPaginationChange: setSourcesPagination,
    state: {
      pagination: sourcesPagination,
      sorting: sorting,
      columnFilters: columnFilters,
      columnVisibility: columnVisibility,
      rowSelection: citationsRowSelection,
    },
  });

  const handlePromptDetailsExport = useCallback(async () => {
    const selectedExecutionRows = table
      .getSelectedRowModel()
      .rows.map((r: any) => r.original);
    const selectedSourceRows = citationsTable
      .getSelectedRowModel()
      .rows.map((r: any) => r.original);
    const isExecutionsTab = tab === "executions";
    let executionRowsForExport = executionsTableData;
    let sourceRowsForExport = citationsPagesTable;
    if (
      isExecutionsTab &&
      selectedExecutionRows.length === 0 &&
      currentBrand?.brandId &&
      prompt.id
    ) {
      setIsExecutionsExporting(true);
      try {
        const response = await visabilityAPI.exportPromptExecutions({
          brandId: currentBrand.brandId,
          days: normalizedDays,
          providers: effectiveSelectedProviders,
          promptId: prompt.id,
          search: promptDetailsSearch,
          dateRange: requestDateRange,
        });
        executionRowsForExport = response.executions || [];
      } catch (error) {
        toast.error(t("legacy.there.is.no.data.for.export.ojj9kv"));
        return;
      } finally {
        setIsExecutionsExporting(false);
      }
    }
    if (
      !isExecutionsTab &&
      selectedSourceRows.length === 0 &&
      currentBrand?.brandId &&
      prompt.id
    ) {
      setIsSourcesExporting(true);
      try {
        const response = await visabilityAPI.exportPromptSources({
          brandId: currentBrand.brandId,
          days: normalizedDays,
          providers: effectiveSelectedProviders,
          promptId: prompt.id,
          search: promptDetailsSearch,
          dateRange: requestDateRange,
        });
        sourceRowsForExport = mapSourceRowsForPromptDetails(
          response.sources || [],
        );
      } catch (error) {
        toast.error(t("legacy.there.is.no.data.for.export.ojj9kv"));
        return;
      } finally {
        setIsSourcesExporting(false);
      }
    }
    const exportRows = isExecutionsTab
      ? buildPromptExecutionsExportRows(executionRowsForExport, {
          brandKeyLabels,
          mainBrandKey: mainBrandKey || undefined,
          resolveBrandKey: resolveCompetitorKey,
          selectedBrandKeys,
        })
      : buildPromptSourcesExportRows(sourceRowsForExport);
    const selectedExportRows = isExecutionsTab
      ? buildPromptExecutionsExportRows(selectedExecutionRows, {
          brandKeyLabels,
          mainBrandKey: mainBrandKey || undefined,
          resolveBrandKey: resolveCompetitorKey,
          selectedBrandKeys,
        })
      : buildPromptSourcesExportRows(selectedSourceRows);

    exportToCSV(
      exportRows,
      selectedExportRows,
      isExecutionsTab ? "prompts-executions.csv" : "prompts-sources.csv",
      {
        emptyMessage: t("legacy.there.is.no.data.for.export.ojj9kv"),
        successMessage: t("legacy.csv.is.downloaded.kdpi5p"),
      },
    );
  }, [
    citationsPagesTable,
    citationsTable,
    currentBrand?.brandId,
    effectiveSelectedProviders,
    executionsTableData,
    brandKeyLabels,
    mainBrandKey,
    mapSourceRowsForPromptDetails,
    normalizedDays,
    prompt.id,
    promptDetailsSearch,
    resolveCompetitorKey,
    requestDateRange,
    selectedBrandKeys,
    tab,
    table,
    t,
  ]);

  const competitorsVisibilityMap = useMemo(() => {
    const map = new Map<string, number>();
    Object.entries(data.competitorsVisibility || {}).forEach(([key, value]) => {
      const normalizedKey = resolveCompetitorKey(key, false);
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return;
      const existing = map.get(normalizedKey);
      const next =
        existing === undefined ? numeric : Math.max(existing, numeric);
      map.set(normalizedKey, Math.min(100, next));
    });
    return map;
  }, [competitorAliasToKey, data.competitorsVisibility, mainBrandKey]);

  const competitorsPositionMap = useMemo(() => {
    const averages = new Map<string, number>();
    Object.entries(data.competitorsPosition || {}).forEach(([key, value]) => {
      const normalizedKey = resolveCompetitorKey(key, false);
      const numeric = Number(value);
      if (!normalizedKey || !Number.isFinite(numeric)) return;
      averages.set(normalizedKey, numeric);
    });
    return averages;
  }, [competitorAliasToKey, data.competitorsPosition, mainBrandKey]);

  const competitorsTableOptimized = useMemo(() => {
    const PROMPT_DETAILS_COMPETITORS_LIMIT = 5;
    const uniqueByKey = new Map<string, { key: string; label: string }>();

    if (mainBrandKey) {
      uniqueByKey.set(mainBrandKey, {
        key: mainBrandKey,
        label: brandKeyLabels.get(mainBrandKey) || mainBrandKey,
      });
    }

    competitorsData.forEach((competitor: any) => {
      const key = normalizeBrandKey(
        competitor.brandKey || competitor.brandDisplay || competitor.domain,
      );
      if (!key) return;
      if (!uniqueByKey.has(key)) {
        uniqueByKey.set(key, {
          key,
          label: brandKeyLabels.get(key) || key,
        });
      }
    });

    const uniqueByLabel = new Map<string, { key: string; label: string }>();
    Array.from(uniqueByKey.values()).forEach((entry) => {
      const labelKey = normalizeBrandKey(entry.label);
      const existing = uniqueByLabel.get(labelKey);
      if (!existing) {
        uniqueByLabel.set(labelKey, entry);
        return;
      }
      if (entry.key === mainBrandKey) {
        uniqueByLabel.set(labelKey, entry);
      }
    });

    let filteredcompetitorsData = Array.from(uniqueByLabel.values())
      .map((item: { key: string; label: string }) => {
        const resolvedItemKey = resolveCompetitorKey(item.key, false);
        const domainHint =
          brandKeyToDomain.get(item.key) ||
          brandKeyToDomain.get(resolvedItemKey) ||
          item.label;
        const canonicalDomainHint = canonicalizeDomain(domainHint) || "";
        const domainStem = canonicalDomainHint
          ? canonicalDomainHint.split(".")[0]
          : "";
        const positionKeyCandidates = Array.from(
          new Set(
            [
              item.key,
              resolvedItemKey,
              resolveCompetitorKey(item.label, false),
              resolveCompetitorKey(domainHint, false),
              canonicalDomainHint ? normalizeBrandKey(canonicalDomainHint) : "",
              domainStem ? normalizeBrandKey(domainStem) : "",
            ].filter(Boolean),
          ),
        );
        const isMainItem = Boolean(
          mainBrandKey &&
          (item.key === mainBrandKey || resolvedItemKey === mainBrandKey),
        );
        const mainVisibility =
          typeof data.mainBrandVisibility === "number"
            ? data.mainBrandVisibility
            : null;
        const baseVisibility =
          isMainItem && mainVisibility !== null
            ? mainVisibility
            : (competitorsVisibilityMap.get(item.key) ??
              competitorsVisibilityMap.get(resolvedItemKey) ??
              0);
        const visibility = Number.isFinite(baseVisibility) ? baseVisibility : 0;
        const rounded = Math.round(visibility);
        let rawPosition: number | undefined;
        for (const key of positionKeyCandidates) {
          const candidate = competitorsPositionMap.get(key);
          if (Number.isFinite(candidate)) {
            rawPosition = candidate;
            break;
          }
        }
        const position = Number.isFinite(rawPosition) ? rawPosition : null;
        return {
          domain: item.label,
          faviconDomain:
            brandKeyToDomain.get(item.key) ||
            brandKeyToDomain.get(resolvedItemKey),
          points: `${rounded}%`,
          visability: rounded,
          position,
          isOwnBrand: isMainItem,
        };
      })
      .sort(
        (a: { visability: number }, b: { visability: number }) =>
          b.visability - a.visability,
      );

    if (mainBrandKey) {
      const hasBrand = filteredcompetitorsData.some(
        (item: { domain: string }) =>
          item.domain === (brandKeyLabels.get(mainBrandKey) || mainBrandKey),
      );
      if (!hasBrand) {
        filteredcompetitorsData.unshift({
          domain: brandKeyLabels.get(mainBrandKey) || mainBrandKey,
          faviconDomain: brandKeyToDomain.get(mainBrandKey),
          points: "0%",
          visability: 0,
          position: null,
          isOwnBrand: true,
        });
      }
    }

    const mainBrandIndex = filteredcompetitorsData.findIndex(
      (item: { isOwnBrand?: boolean }) => Boolean(item.isOwnBrand),
    );
    if (filteredcompetitorsData.length > PROMPT_DETAILS_COMPETITORS_LIMIT) {
      if (mainBrandIndex >= PROMPT_DETAILS_COMPETITORS_LIMIT) {
        const mainBrandItem = filteredcompetitorsData[mainBrandIndex];
        const topWithMain = filteredcompetitorsData
          .filter((_, index) => index !== mainBrandIndex)
          .slice(0, PROMPT_DETAILS_COMPETITORS_LIMIT - 1);
        filteredcompetitorsData = [...topWithMain, mainBrandItem].sort(
          (a: { visability: number }, b: { visability: number }) =>
            b.visability - a.visability,
        );
      } else {
        filteredcompetitorsData = filteredcompetitorsData.slice(
          0,
          PROMPT_DETAILS_COMPETITORS_LIMIT,
        );
      }
    }

    return filteredcompetitorsData;
  }, [
    competitorsData,
    data.mainBrandVisibility,
    mainBrandKey,
    brandKeyLabels,
    competitorsVisibilityMap,
    competitorsPositionMap,
    brandKeyToDomain,
  ]);

  const chartSeriesOrder = useMemo(() => {
    return competitorsTableOptimized.map(
      (item: { domain: string; faviconDomain?: string }) => {
        return (
          canonicalizeDomain(item.faviconDomain || item.domain) || item.domain
        );
      },
    );
  }, [competitorsTableOptimized]);
  const chartBarValuesBySeries = useMemo(() => {
    const values: Record<string, number> = {};
    competitorsTableOptimized.forEach(
      (item: {
        domain: string;
        visability?: number;
        faviconDomain?: string;
      }) => {
        const key =
          canonicalizeDomain(item.faviconDomain || item.domain) || item.domain;
        const numeric = Number(item.visability);
        if (!Number.isFinite(numeric)) return;
        values[key] = Math.min(100, Math.max(0, Math.round(numeric)));
      },
    );
    return values;
  }, [competitorsTableOptimized]);

  return (
    <Sheet
      open={sheetOpen}
      onOpenChange={(value) => {
        onOpenChange?.(value);
      }}
    >
      <SheetContent className="w-full sm:max-w-6xl p-4 overflow-y-scroll duration-700 pb-7">
        <div className="flex justify-between bg-white z-10 flex-wrap gap-2">
          <div className="flex gap-2 items-center">
            <SheetClose asChild>
              <Button
                onClick={() => onOpenChange?.(false)}
                variant="outline"
                className="cursor-pointer"
              >
                <ArrowLeft />
                <LocalizedText id="legacy.back.yjmdam" />
              </Button>
            </SheetClose>
            <Separator className="mx-3" orientation="vertical" />
            <div className="font-semibold text-base text-[#0A0A0A]">
              <LocalizedText id="legacy.prompt.details.x9lf37" />
            </div>
          </div>
          <div className="flex gap-2">
            <DateRangeFilter
              onTimeRangeChange={handleSetTimeRange}
              value={dateRange}
              onValueChange={setDateRange}
            />

            <MultiSelect
              options={availablePlatformOptions}
              selected={selectedPlatforms}
              isAllSelected={
                <div className="flex items-center gap-2">
                  <Bot className="text-black"></Bot>
                  <LocalizedText id="legacy.all.platforms.2apns" />
                </div>
              }
              onChange={setSelectedPlatforms}
              placeholder={t("legacy.all.platforms.2apns")}
            >
              {" "}
              <Bot />
            </MultiSelect>
          </div>
        </div>
        <SheetHeader className="flex flex-row justify-between border rounded-md flex-wrap ">
          <SheetTitle className="sr-only">
            <LocalizedText id="legacy.prompt.details.x9lf37" />
          </SheetTitle>
          <div className="text-md text-lg font-semibold text-[#0A0A0A]">
            {prompt.text}
          </div>
          <div className="flex gap-3">
            <div className="flex items-center">
              <Separator orientation="vertical" />
              <span className="ml-4 mr-3 text-muted-foreground text-sm font-normal">
                <LocalizedText id="legacy.country.1oy8cul" />
              </span>
              {countries.find((cnt) => cnt.code === prompt.country)?.icon}
              {prompt.country}
            </div>
            <div className="flex items-center">
              <Separator orientation="vertical" />
              <span className="ml-4 mr-3 text-muted-foreground text-sm font-normal">
                <LocalizedText id="legacy.status.1iwhbo1" />
              </span>
              <Select
                value={status}
                onValueChange={(value) => handleStatusChange(value)}
              >
                <SelectTrigger className="w-[135px]">
                  <SelectValue
                    placeholder={t("legacy.select.status.192z8nt")}
                    className="flex items-center gap-2"
                  >
                    {promptStatus.find((opt) => opt.value === status)?.icon}
                    {promptStatus.find((opt) => opt.value === status)?.label}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {promptStatus.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        {option.icon}
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </SheetHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChartAreaInteractive
            setTimeRange={setTimeRange}
            loading={
              isLoading ||
              isCompetitorsLoading ||
              (!data.promptRunCount &&
                (data.promptStatus === "running" ||
                  data.promptStatus === "queued"))
            }
            showDescription={false}
            timeRange={timeRange}
            seriesOrder={chartSeriesOrder}
            barValuesBySeries={chartBarValuesBySeries}
            chartData={data.chartData.map(
              (entry: Record<string, number | string>) => {
                const allowedKeys = Array.from(
                  new Set(
                    [
                      mainBrandKey,
                      ...competitorsData.map((item: { domain: string }) =>
                        normalizeBrandKey(
                          (item as any).brandKey ||
                            (item as any).brandDisplay ||
                            item.domain,
                        ),
                      ),
                    ].filter((key): key is string => Boolean(key)),
                  ),
                );
                const allowedKeySet = new Set(allowedKeys);

                const aggregatedByKey = new Map<string, number>();
                Object.entries(entry).forEach(([key, value]) => {
                  if (key === "date") return;
                  const normalizedKey = resolveCompetitorKey(key, false);
                  if (!normalizedKey || !allowedKeySet.has(normalizedKey))
                    return;
                  const numeric = Number(value);
                  if (!Number.isFinite(numeric)) return;
                  const existing = aggregatedByKey.get(normalizedKey);
                  aggregatedByKey.set(
                    normalizedKey,
                    existing === undefined
                      ? numeric
                      : Math.max(existing, numeric),
                  );
                });

                const newEntry: Record<string, number | string> = {
                  date: entry.date,
                };
                allowedKeys.forEach((key: string) => {
                  const label =
                    brandKeyToChartLabel.get(key) ||
                    canonicalizeDomain(brandKeyToDomain.get(key)) ||
                    brandKeyLabels.get(key) ||
                    key;
                  const value = aggregatedByKey.get(key) ?? 0;
                  newEntry[label] = Math.min(100, Math.round(value));
                });
                return newEntry;
              },
            )}
            currentBrand={currentBrandDomain}
          ></ChartAreaInteractive>
          <SimpleTable
            currentBrand={currentBrand}
            isRunResultLoading={isLoading || isCompetitorsLoading}
            data={competitorsTableOptimized}
            maxTableHeight="281px"
            tooltip={{
              text: "Compare your brand’s visibility across selected competitors.",
              icon: <Info className="h-5 w-5 text-muted-foreground" />,
            }}
            title={t("legacy.competitors.94sz4m")}
            mainColumnName="Brand"
            middleColumn="Position"
          ></SimpleTable>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-4">
          <div className="p-7 rounded-lg border shadow-sm">
            <ExtendedTable
              pageName="promptsDetails"
              pagination={false}
              table={allQueriesTable}
              loading={isLoading}
              columns={2}
              tooltip={{
                description:
                  "Most frequently used web search queries triggered by your prompts",
              }}
              hiddenTitle={true}
              title={t("legacy.ai.web.search.queries.9cgnxb")}
              description={t(
                "legacy.most.frequently.used.queries.triggered.by.your.prompts.3uxqgg",
              )}
              noDataText={{
                title: "No web search queries available",
                description:
                  "Web search was not triggered for this prompt or is not supported for the selected platform",
              }}
            ></ExtendedTable>
          </div>
          <Card className="pb-0 gap-0">
            <CardHeader className="pb-2">
              <CardTitle>
                <LocalizedText id="legacy.domains.by.source.type.1ldfuyw" />
              </CardTitle>
              <CardDescription>
                <LocalizedText id="legacy.share.of.cited.domains.by.source.type.1nzwm43" />
              </CardDescription>
            </CardHeader>
            <CardContent className="h-[320px]">
              {!hasSourcesByDomainTypeData && !isLoading ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                  <LocalizedText id="legacy.no.source.data.yet.9ngg1t" />
                </div>
              ) : isLoading ? (
                <div className="relative w-full h-[200px]">
                  <div className="z-10 gap-1 flex-col absolute flex justify-center items-center w-full h-full">
                    <div className="flex gap-2 items-center flex-col p-2 bg-muted rounded-md">
                      <Spinner
                        className="text-[#FF5E00]"
                        variant="circle"
                      ></Spinner>
                    </div>
                    <div>
                      <LocalizedText id="legacy.processing.3jbdvy" />
                    </div>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sourcesByDomainTypeData}
                    layout="vertical"
                    margin={{ top: 6, right: 44, left: 4, bottom: 6 }}
                  >
                    <CartesianGrid
                      vertical={false}
                      horizontal={false}
                      strokeDasharray="3 3"
                    />
                    <XAxis
                      type="number"
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={() => ``}
                    />
                    <YAxis
                      type="category"
                      dataKey="type"
                      tickLine={false}
                      axisLine={false}
                      width={88}
                    />
                    <RechartsTooltip
                      cursor={{ fill: "rgba(255,255,255,0.08)" }}
                      content={({ active, payload }) => {
                        const item = payload?.[0]?.payload as
                          | {
                              type: SourceDomainType;
                              percentage: number;
                              color: string;
                              topDomains: string[];
                            }
                          | undefined;
                        if (!active || !item) return null;
                        return (
                          <div className="rounded-lg border border-white/10 bg-black px-3 py-2 text-xs shadow-xl">
                            <div className="flex items-center gap-2 text-white font-medium">
                              <span
                                className="h-2.5 w-2.5 rounded-[2px] shrink-0"
                                style={{ backgroundColor: item.color }}
                              />
                              <span>
                                {item.type} - {item.percentage}%
                              </span>
                            </div>
                            <div className="mt-1 text-zinc-300 truncate">
                              {item.topDomains?.length > 0 && (
                                <div className="space-y-1">
                                  <ul className="space-y-0.5">
                                    {item.topDomains.map((domain, i) => (
                                      <li
                                        key={i}
                                        className="truncate flex gap-2"
                                      >
                                        <img
                                          className="w-4 rounded-md border"
                                          src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(domain) || ""}&size=64`}
                                          alt="logo"
                                        />
                                        {domain}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="percentage"
                      isAnimationActive={false}
                      radius={[0, 4, 4, 0]}
                      barSize={24}
                      background={{ fill: "#F5F5F5" }}
                    >
                      {sourcesByDomainTypeData.map((entry) => (
                        <Cell key={entry.type} fill={entry.color} />
                      ))}
                      <LabelList
                        dataKey="percentage"
                        position="right"
                        formatter={(val) => `${val}%`}
                        style={{ fontSize: 14, fill: "#000" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="flex w-full flex-col gap-6 mb-4">
          <Tabs value={tab} onValueChange={setTab}>
            <div className="text-sm text-muted-foreground mb-1">
              <LocalizedText id="legacy.view.full.ai.responses.in.executions.and.their.eb0aiy" />
            </div>
            <div className="flex justify-between w-full items-center flex-wrap gap-2">
              <TabsList>
                <TabsTrigger className="cursor-pointer" value="executions">
                  <LocalizedText id="legacy.executions.1r917z8" />
                </TabsTrigger>
                <TabsTrigger className="cursor-pointer" value="citations">
                  <LocalizedText id="legacy.sources.1ax6mej" />
                </TabsTrigger>
              </TabsList>
              <div className="flex gap-2">
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    type="text"
                    placeholder={t("legacy.search.14mmb9h")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-8 max-w-sm min-w-[200px]"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="px-0 cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                <Separator
                  orientation="vertical"
                  className="data-[orientation=vertical]:h-[35px]"
                />
                <Button
                  className="cursor-pointer"
                  variant="outline"
                  onClick={handlePromptDetailsExport}
                  disabled={isSourcesExporting || isExecutionsExporting}
                >
                  <Download></Download>{" "}
                  <LocalizedText id="legacy.export.1305875" />
                </Button>
              </div>
            </div>
            <TabsContent value="executions">
              <ExtendedTable
                pageName="promptsDetails"
                pagination={true}
                table={table}
                loading={
                  isLoading || isExecutionsLoading || isExecutionsFetching
                }
                columns={5}
                noDataText={executionsNoDataText}
              ></ExtendedTable>
            </TabsContent>
            <TabsContent value="citations">
              <div className="flex flex-col lg:flex-row gap-4">
                <ExtendedTable
                  pagination={true}
                  table={citationsTable}
                  loading={isLoading || isSourcesLoading || isSourcesFetching}
                  columns={promptsDetailsCitationsColumns.length}
                  noDataText={citationsNoDataText}
                ></ExtendedTable>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        <ExecutionDetailsSheet
          open={Boolean(executionDetailsOpenRun)}
          onOpenChange={(open) => {
            if (!open) {
              setExecutionDetailsOpenRun(null);
            }
          }}
          run={selectedExecutionDetailsRun}
          responseLoading={isExecutionDetailsResponseLoading}
          country={prompt.country}
          text={prompt.text}
        />
        <div
          className={`
        pointer-events-none z-100 
        fixed bottom-0 right-0 w-full sm:max-w-6xl h-16
        bg-gradient-to-t from-background via-background/90 to-transparent
        transition-opacity duration-500
      `}
        />
      </SheetContent>
    </Sheet>
  );
}
